import { useMutation, useQuery } from '@tanstack/react-query';
import { api, errorMessage } from '../lib/api';
import { APP_NAME } from '../lib/app-name';
import { Button, Card, ErrorText } from '../ui';

interface ClientInfo {
  client_name: string;
  client_uri: string | null;
  redirect_host: string;
  verified: boolean;
  tenants: Array<{ id: string; name: string }>;
}

/**
 * Consenso OAuth per un assistente AI (Claude, ChatGPT…): chi chiede e dove torna. L'accesso vale sempre per
 * tutto l'account (tutte le aziende, anche quelle nuove), con i permessi dell'utente.
 */
export function AuthorizePage() {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const info = useQuery({
    queryKey: ['oauth-client', clientId, redirectUri],
    queryFn: () => api<ClientInfo>(`/mcp/oauth/client?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`),
    retry: false,
  });
  const decide = useMutation({
    mutationFn: (decision: 'allow' | 'deny') =>
      api<{ redirect_url: string }>('/mcp/oauth/decision', {
        body: {
          client_id: clientId,
          redirect_uri: redirectUri,
          state: params.get('state') ?? undefined,
          code_challenge: params.get('code_challenge') ?? '',
          code_challenge_method: params.get('code_challenge_method') ?? '',
          resource: params.get('resource') ?? undefined,
          tenant_id: null,
          decision,
        },
      }),
    onSuccess: (r) => window.location.assign(r.redirect_url),
  });

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <h1 className="mb-1 text-lg font-semibold">Collegare un assistente AI</h1>
        {info.isLoading && <p className="text-sm text-ink/60">Verifica della richiesta…</p>}
        <ErrorText>{info.error ? errorMessage(info.error) : ''}</ErrorText>
        {info.data && (
          <div className="space-y-4 text-sm">
            <p>
              <strong>{info.data.client_name}</strong> chiede di accedere a {APP_NAME} con il tuo account.
            </p>
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Nome dichiarato dall’assistente, non verificato. Dopo il consenso tornerai su <strong>{info.data.redirect_host}</strong>: prosegui solo se hai avviato tu il collegamento da lì.
            </p>
            <ul className="list-inside list-disc text-ink/70">
              <li>potrà fare ciò che fai tu dalla web app, su tutte le tue aziende (anche quelle che creerai): creare e configurare aziende, gestire persone, processi, proposte, archivio;</li>
              <li>avrà i tuoi stessi permessi in ogni azienda;</li>
              <li>potrà chiudere solo i tuoi compiti, sempre con una prova;</li>
              <li>per le azioni verso l’esterno e per quelle che eliminano qualcosa ti chiederà conferma;</li>
              <li>password e credenziali dei programmi restano solo nella web app;</li>
              <li>puoi revocare l’accesso quando vuoi da Account.</li>
            </ul>
            <div className="flex gap-2">
              <Button disabled={decide.isPending} onClick={() => decide.mutate('allow')}>
                Consenti
              </Button>
              <Button variant="ghost" disabled={decide.isPending} onClick={() => decide.mutate('deny')}>
                Rifiuta
              </Button>
            </div>
            <ErrorText>{decide.error ? errorMessage(decide.error) : ''}</ErrorText>
          </div>
        )}
      </Card>
    </main>
  );
}
